import type { ReferenceCell } from 'entities/document';

export interface CellRef {
  row: number;
  col: number;
}

export interface LabelAwareDiff {
  changedNew: CellRef[];
  changedOld: CellRef[];
  matchedRows: number;
}

const norm = (v: unknown): string =>
  String(v ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const rowsByLabel = (grid: ReferenceCell[][]): Map<string, number[]> => {
  const groups = new Map<string, number[]>();
  grid.forEach((row, i) => {
    if (i === 0) return;
    const label = norm(row?.[0]?.value);
    if (!label) return;
    const rows = groups.get(label) ?? [];
    rows.push(i);
    groups.set(label, rows);
  });
  return groups;
};

const columnsByHeader = (grid: ReferenceCell[][]): Map<string, number> => {
  const map = new Map<string, number>();
  (grid[0] ?? []).forEach((cell, c) => {
    const header = norm(cell?.value);
    if (c >= 1 && header && !map.has(header)) map.set(header, c);
  });
  return map;
};

export function labelAwareDiff(oldGrid: ReferenceCell[][] = [], newGrid: ReferenceCell[][] = []): LabelAwareDiff {
  const changedNew: CellRef[] = [];
  const changedOld: CellRef[] = [];
  const oldRows = rowsByLabel(oldGrid);
  const newRows = rowsByLabel(newGrid);
  const oldCols = columnsByHeader(oldGrid);
  const newHeaders = new Set(columnsByHeader(newGrid).keys());
  const matchedOldRows = new Set<number>();

  newGrid.forEach((row, r) => {
    if (r === 0) return;
    const label = norm(row?.[0]?.value);
    if (!label) return;
    const oldR = (oldRows.get(label) ?? [])[(newRows.get(label) ?? []).indexOf(r)];
    if (oldR === undefined) {
      row.forEach((_, c) => c >= 1 && changedNew.push({ row: r, col: c }));
      return;
    }
    matchedOldRows.add(oldR);
    row.forEach((cell, c) => {
      if (c < 1) return;
      const header = norm(newGrid[0]?.[c]?.value);
      const oldC = header ? oldCols.get(header) : c;
      const oldValue = oldC === undefined ? undefined : oldGrid[oldR]?.[oldC]?.value;
      if (norm(oldValue) !== norm(cell?.value)) {
        changedNew.push({ row: r, col: c });
        if (oldC !== undefined) changedOld.push({ row: oldR, col: oldC });
      }
    });
    (oldGrid[oldR] ?? []).forEach((_, c) => {
      const header = norm(oldGrid[0]?.[c]?.value);
      if (c >= 1 && header && !newHeaders.has(header)) changedOld.push({ row: oldR, col: c });
    });
  });

  oldGrid.forEach((row, r) => {
    if (r === 0 || matchedOldRows.has(r) || !norm(row?.[0]?.value)) return;
    row.forEach((_, c) => c >= 1 && changedOld.push({ row: r, col: c }));
  });

  return { changedNew, changedOld, matchedRows: matchedOldRows.size };
}
